import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.easyfacture.easyclick-solution.fr';

const api = axios.create({ baseURL: API_URL });

api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  response => response.data,
  error => {
    if (error.response?.status === 401 && !error.config?.url?.includes('/auth/login')) {
      localStorage.removeItem('token');
      window.location.href = '/#/login';
    }
    return Promise.reject(error.response?.data || error);
  }
);

// Auth
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  me: () => api.get('/auth/me'),
  updatePreferences: (data) => api.put('/auth/preferences', data),
  updateProfile: (data) => api.put('/auth/profile', data),
  updateAppSettings: (data) => api.put('/auth/app-settings', data)
};

// Clients
export const clientsAPI = {
  list: (params) => api.get('/clients', { params }),
  get: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
  stats: (id) => api.get(`/clients/${id}/stats`)
};

// Invoices
export const invoicesAPI = {
  list: (params) => api.get('/invoices', { params }),
  get: (id) => api.get(`/invoices/${id}`),
  create: (data) => api.post('/invoices', data),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  updateStatus: (id, status) => api.patch(`/invoices/${id}/status`, { status }),
  delete: (id) => api.delete(`/invoices/${id}`),
  addPayment: (id, data) => api.post(`/invoices/${id}/payments`, data),
  stats: () => api.get('/invoices/stats')
};

// Quotes
export const quotesAPI = {
  list: (params) => api.get('/quotes', { params }),
  get: (id) => api.get(`/quotes/${id}`),
  create: (data) => api.post('/quotes', data),
  update: (id, data) => api.put(`/quotes/${id}`, data),
  updateStatus: (id, status) => api.patch(`/quotes/${id}/status`, { status }),
  delete: (id) => api.delete(`/quotes/${id}`),
  convert: (id) => api.post(`/quotes/${id}/convert`)
};

// Products
export const productsAPI = {
  list: (params) => api.get('/products', { params }),
  get: (id) => api.get(`/products/${id}`),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`)
};

// Deliveries
export const deliveriesAPI = {
  list: (params) => api.get('/deliveries', { params }),
  get: (id) => api.get(`/deliveries/${id}`),
  create: (data) => api.post('/deliveries', data),
  update: (id, data) => api.put(`/deliveries/${id}`, data),
  updateStatus: (id, status) => api.patch(`/deliveries/${id}/status`, { status }),
  convertToInvoice: (id) => api.post(`/deliveries/${id}/convert-to-invoice`),
  delete: (id) => api.delete(`/deliveries/${id}`)
};

// Letters of change (lettres de change)
export const lettersOfChangeAPI = {
  list: (params) => api.get('/letters-of-change', { params }),
  get: (id) => api.get(`/letters-of-change/${id}`),
  create: (data) => api.post('/letters-of-change', data),
  update: (id, data) => api.put(`/letters-of-change/${id}`, data),
  updateStatus: (id, status) => api.patch(`/letters-of-change/${id}/status`, { status }),
  delete: (id) => api.delete(`/letters-of-change/${id}`),
  stats: () => api.get('/letters-of-change/stats')
};

// Returns
export const returnsAPI = {
  list: (params) => api.get('/returns', { params }),
  get: (id) => api.get(`/returns/${id}`),
  create: (data) => api.post('/returns', data),
  update: (id, data) => api.put(`/returns/${id}`, data),
  updateStatus: (id, status) => api.patch(`/returns/${id}/status`, { status }),
  delete: (id) => api.delete(`/returns/${id}`)
};

// Company
export const companyAPI = {
  get: () => api.get('/company'),
  update: (data) => api.put('/company', data)
};

// Dashboard
export const dashboardAPI = {
  overview: () => api.get('/dashboard/overview')
};

export default api;

// POS
export const posAPI = {
  searchProducts: (params) => api.get('/pos/products/search', { params }),
  featuredProducts: () => api.get('/pos/products/featured'),
  list: (params) => api.get('/pos', { params }),
  get: (id) => api.get(`/pos/${id}`),
  create: (data) => api.post('/pos', data),
  refund: (id) => api.post(`/pos/${id}/refund`),
  todayStats: () => api.get('/pos/stats/today'),
};

// Stock
export const stockAPI = {
  overview: () => api.get('/stock/overview'),
  alerts: () => api.get('/stock/alerts'),
  movements: (params) => api.get('/stock/movements', { params }),
  productHistory: (productId) => api.get(`/stock/product/${productId}/history`),
  addMovement: (data) => api.post('/stock/movement', data),
  adjust: (data) => api.post('/stock/adjust', data),
};

// Suppliers
export const suppliersAPI = {
  list: (params) => api.get('/suppliers', { params }),
  get: (id) => api.get(`/suppliers/${id}`),
  create: (data) => api.post('/suppliers', data),
  update: (id, data) => api.put(`/suppliers/${id}`, data),
  delete: (id) => api.delete(`/suppliers/${id}`),
};

// Purchase Orders
export const purchaseOrdersAPI = {
  list: (params) => api.get('/purchase-orders', { params }),
  get: (id) => api.get(`/purchase-orders/${id}`),
  create: (data) => api.post('/purchase-orders', data),
  update: (id, data) => api.put(`/purchase-orders/${id}`, data),
  receive: (id, data) => api.post(`/purchase-orders/${id}/receive`, data),
  cancel: (id) => api.post(`/purchase-orders/${id}/cancel`),
  delete: (id) => api.delete(`/purchase-orders/${id}`),
};

export const printersAPI = {
  list: () => api.get('/printers'),
};